import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, MapPin, CreditCard, AlertCircle, Pencil, RefreshCw } from 'lucide-react';
import { Img } from './Img';
import { SideMenuModal } from './SideMenuModal';
import { colors } from '../constants/colors';
import { getMyOrderForShow, getPaymentMethods, retrySalePayment } from '../api/client';
import type { Order, PaymentMethod } from '../types';

interface Props {
  showId: string;
  onClose: () => void;
  onPaymentFixed?: () => void;
}

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  AUTHORIZED: '#22C55E',
  CAPTURED: '#22C55E',
  FAILED: '#EF4444',
  CANCELLED: '#6B7280',
  REFUNDED: '#F59E0B',
  PENDING: '#F59E0B',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  AUTHORIZED: 'Authorized',
  CAPTURED: 'Paid',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
  PENDING: 'Processing',
};

export function PurchasesModal({ showId, onClose, onPaymentFixed }: Props) {
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [defaultCard, setDefaultCard] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, methods] = await Promise.all([
        getMyOrderForShow(showId),
        getPaymentMethods().catch(() => [] as PaymentMethod[]),
      ]);
      setOrder(o);
      setDefaultCard(methods.find(m => m.isDefault) || methods[0] || null);
    } catch (err) {
      console.error('[Purchases] load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  const handleRetryAll = async () => {
    if (!order) return;
    const failed = order.itemSales.filter(s => s.paymentStatus === 'FAILED');
    if (failed.length === 0) return;
    setRetrying(true);
    setRetryResult(null);
    let ok = 0;
    for (const s of failed) {
      try {
        await retrySalePayment(s.id);
        ok++;
      } catch (err) {
        console.error('[Purchases] retry failed', s.id, err);
      }
    }
    await load();
    setRetrying(false);
    if (ok === failed.length) {
      setRetryResult('All failed payments processed.');
      onPaymentFixed?.();
    } else if (ok > 0) {
      setRetryResult(`${ok} of ${failed.length} retried. Some still need attention.`);
    } else {
      setRetryResult('Unable to process payment. Try updating your payment method.');
    }
  };

  const sales = order?.itemSales || [];
  const subtotal = sales.reduce((sum, s) => sum + s.winningBid, 0);
  const shipping = sales.reduce((sum, s) => sum + s.shippingCost - (s.shippingDiscount || 0), 0);
  const tax = sales.reduce((sum, s) => sum + s.tax, 0);
  const total = sales.reduce((sum, s) => sum + s.total, 0);

  return (
    <SideMenuModal title="My Purchases" onClose={onClose} maxWidth={520}>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: colors.gray[600] }}>Loading...</div>
      ) : !order || sales.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Package size={40} color={colors.gray[300]} style={{ margin: '0 auto' }} />
          <p style={{ marginTop: 12, color: colors.gray[600], fontSize: 14 }}>No purchases yet in this show.</p>
        </div>
      ) : (
        <>
          {order.hasFailedPayments && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 16, padding: 16, marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <AlertCircle size={16} color="#EF4444" />
                <span style={{ fontWeight: 700, fontSize: 14, color: '#DC2626' }}>Fix Payment</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <CreditCard size={16} color={colors.gray[600]} />
                  <span style={{ fontSize: 14, color: colors.ink.black, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {defaultCard
                      ? `${defaultCard.cardBrand.charAt(0).toUpperCase() + defaultCard.cardBrand.slice(1)} •••• ${defaultCard.cardLast4}`
                      : 'No payment method'}
                  </span>
                </div>
                <button
                  onClick={() => navigate('/payment-methods')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '6px 10px', background: colors.gray[100],
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    color: colors.gray[600], fontSize: 12, fontWeight: 600,
                  }}
                >
                  <Pencil size={12} /> Edit
                </button>
              </div>
              <button
                onClick={handleRetryAll}
                disabled={retrying}
                style={{
                  width: '100%', background: '#EF4444', color: '#fff',
                  border: 'none', borderRadius: 10,
                  padding: '12px 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontWeight: 600, fontSize: 14,
                  cursor: retrying ? 'default' : 'pointer',
                  opacity: retrying ? 0.7 : 1,
                }}
              >
                <RefreshCw size={16} />
                {retrying ? 'Retrying…' : 'Retry Failed Payments'}
              </button>
              {retryResult && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#991B1B' }}>{retryResult}</div>
              )}
            </div>
          )}

          <Card title={`Items (${sales.length})`} icon={<Package size={14} color={colors.gray[600]} />}>
            {sales.map((sale, idx) => (
              <div
                key={sale.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 0',
                  borderTop: idx > 0 ? `1px solid ${colors.gray[200]}` : 'none',
                }}
              >
                {sale.itemSnapshot?.imageUrl ? (
                  <Img src={sale.itemSnapshot.imageUrl} alt="" variant="thumb" renderedWidth={40} blurhash={sale.itemSnapshot.blurhash} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: 8,
                    background: colors.gray[200],
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Package size={18} color={colors.gray[400]} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600, fontSize: 14, color: colors.ink.black,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {sale.itemSnapshot?.name || 'Item'}
                  </div>
                  {sale.paymentStatus && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                      <div style={{
                        width: 6, height: 6, borderRadius: 3,
                        background: PAYMENT_STATUS_COLORS[sale.paymentStatus] || colors.gray[400],
                      }} />
                      <span style={{ fontSize: 11, color: PAYMENT_STATUS_COLORS[sale.paymentStatus] || colors.gray[600] }}>
                        {PAYMENT_STATUS_LABELS[sale.paymentStatus] || sale.paymentStatus}
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, color: colors.ink.black, flexShrink: 0 }}>
                  ${sale.winningBid.toFixed(2)}
                </div>
              </div>
            ))}
          </Card>

          {order.recipientName && (
            <Card title="Shipping To" icon={<MapPin size={14} color={colors.gray[600]} />}>
              <div style={{ fontWeight: 600, fontSize: 14, color: colors.ink.black, marginBottom: 4 }}>
                {order.recipientName}
              </div>
              <div style={{ color: colors.gray[600], fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                {order.streetLine1}
                {order.streetLine2 ? `\n${order.streetLine2}` : ''}
                {`\n${order.city}, ${order.state} ${order.zipCode}`}
              </div>
            </Card>
          )}

          <Card title="Payment Summary" icon={<CreditCard size={14} color={colors.gray[600]} />} last>
            <SummaryRow label={`Subtotal (${sales.length} item${sales.length !== 1 ? 's' : ''})`} value={`$${subtotal.toFixed(2)}`} />
            <SummaryRow label="Shipping" value={`$${shipping.toFixed(2)}`} />
            <SummaryRow label="Tax" value={`$${tax.toFixed(2)}`} />
            <div style={{ borderTop: `1px solid ${colors.gray[200]}`, marginTop: 6, paddingTop: 8 }}>
              <SummaryRow label="Total" value={`$${total.toFixed(2)}`} bold />
            </div>
          </Card>
        </>
      )}
    </SideMenuModal>
  );
}

function Card({ title, icon, children, last }: { title: string; icon: React.ReactNode; children: React.ReactNode; last?: boolean }) {
  return (
    <div style={{
      background: colors.gray[100], borderRadius: 16, padding: 16,
      marginBottom: last ? 0 : 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        {icon}
        <span style={{
          fontSize: 12, fontWeight: 600, color: colors.gray[600],
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function SummaryRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ color: bold ? colors.ink.black : colors.gray[600], fontSize: 13, fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ color: colors.ink.black, fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}
